import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Dialog, Button, TextField } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import { useStore } from 'app/store'
import CircularProgress from '@mui/material/CircularProgress'
import { debounce } from 'lodash'
import Axios from 'axios'
import { toast } from 'react-toastify'

const inputLabelProps = {
  classes: {
    shrink: 'text-black font-bold',
    focused: 'text-primary',
  },
}

const SelectFolderDialog = ({ open, onClose, objectType, object }) => {
  const { folders } = useStore()
  const cancelTokenSource = useRef()
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingOptions, setLoadingOptions] = useState(false)
  const [errored, setErrored] = useState(false)

  const [inputValue, setInputValue] = useState('')
  const [folderOptionsList, setFolderOptionsList] = useState([])
  const [oldFolder, setOldFolder] = useState(null)
  const [folder, setFolder] = useState(null)
  const objectId = object.id
  const projectType = objectType == 'PROJECT' ? object?.type : '';
  const loadData = async () => {
    setLoading(true)
    setErrored(false)
    try {
      const response = await folders.getFolderInfo(objectType, objectId)
      setOldFolder(response?.id ? response : null)
      setFolder(response?.id ? response : null)
    } catch (error) {
      setErrored(true)
    } finally {
      setLoading(false)
    }
  }

  const loadOptions = async (searchValue) => {
    // eslint-disable-next-line no-unused-expressions
    cancelTokenSource?.current?.cancel?.()
    setLoadingOptions(true)
    try {
      const tokenSource = Axios.CancelToken.source()
      cancelTokenSource.current = tokenSource
      const options = await folders.searchFolderByName(searchValue, tokenSource)
      // const folderOptionsList = options.map((folder) => folder)
      setFolderOptionsList(options || [])
    } catch (error) {
    } finally {
      setLoadingOptions(false)
    }
  }

  const debounceLoadOptions = useCallback(debounce(loadOptions, 400), [])

  useEffect(() => {
    debounceLoadOptions(inputValue)
  }, [debounceLoadOptions, inputValue])

  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSaveClick = async () => {
    if (saving) return
    setSaving(true)
    try {
      const objectTypeText = objectType.replace('_', ' ').toLowerCase()
      if (oldFolder) {
        await folders.deleteFolderItem(objectType, objectId, projectType)
        if (!folder) {
          toast.success(
            `${object.name} ${objectTypeText} removed from ${oldFolder.name} folder`
          )
        }
      }
      if (folder) {
        await folders.createFolderItems(folder?.id, [
          { type: objectType, objectId, order: 1, ownerUserId: object.ownerUserId || object.ownerId },
        ], projectType)
        toast.success(
          `${object.name} ${objectTypeText} moved to ${folder.name} folder`
        )
      }
      onClose()
    } catch (error) {
      console.error('onSaveClick', error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog maxWidth="sm" open={open} onClose={onClose}>
      <h4 className="capitalize m-0 mb-4">Select folder</h4>
      {loading ? (
        <CircularProgress />
      ) : errored ? (
        <span>Failed to load data</span>
      ) : (
        <>
          <Autocomplete
            disabled={saving}
            id="folder"
            options={folderOptionsList}
            loading={loadingOptions}
            value={folder}
            filterSelectedOptions
            getOptionLabel={(folder) => folder.name}
            onChange={(event, value) => {
              setFolder(value)
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                name="folder"
                label="Folder"
                value={inputValue}
                onChange={(event) => {
                  setInputValue(event.target.value)
                }}
                InputLabelProps={inputLabelProps}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loadingOptions ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
                variant="outlined"
                size="small"
                fullWidth
              />
            )}
          />
        </>
      )}

      <div className="flex justify-start pt-2 m--2">
        <Button
          className="m-2 rounded hover-bg-primary px-6"
          variant="outlined"
          color="primary"
          disabled={saving}
          onClick={errored ? loadData : onSaveClick}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {errored ? 'Reload' : 'Save'}
        </Button>
        <Button
          className="m-2 rounded hover-bg-error px-6"
          variant="outlined"
          onClick={onClose}
        >
          Cancel
        </Button>
      </div>
    </Dialog>
  )
}

export default SelectFolderDialog
